import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CreditCard, Clock, CheckCircle2, XCircle, AlertCircle,
  Receipt, FileText, ChevronRight, CalendarCheck, ClipboardList,
  CheckCircle, Plus,
} from 'lucide-react';
import api from '@/lib/axios';
import { formatDate, cn } from '@/lib/utils';
import { PresupuestoFormDialog } from '@/pages/finanzas/presupuestos/PresupuestoFormDialog';

type MetodoPago = 'efectivo' | 'tarjeta' | 'transferencia' | 'stripe';
type EstadoPago = 'pendiente' | 'pagado' | 'reembolsado' | 'cancelado';

interface Pago {
  id: string;
  monto: number;
  metodo: MetodoPago;
  estado: EstadoPago;
  referencia: string | null;
  pagadoEn: string | null;
  createdAt: string;
  consulta?: { id: string; createdAt: string } | null;
  consultaId: string | null;
}

interface Cita {
  id: string;
  fechaInicio: string;
  servicioId?: string | null;
  servicioNombre?: string | null;
  estado: string;
  medico?: { nombre: string } | null;
}

const METODO_LABEL: Record<MetodoPago, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  stripe: 'Stripe',
};

const ESTADO_CONFIG: Record<EstadoPago, { label: string; cls: string; icon: React.ElementType }> = {
  pendiente:   { label: 'Pendiente',   cls: 'bg-amber-50 text-amber-700',  icon: Clock },
  pagado:      { label: 'Pagado',      cls: 'bg-green-50 text-green-700',  icon: CheckCircle2 },
  reembolsado: { label: 'Reembolsado', cls: 'bg-blue-50 text-blue-700',    icon: AlertCircle },
  cancelado:   { label: 'Cancelado',   cls: 'bg-red-50 text-reded-700',    icon: XCircle },
};

function fmtMonto(n: number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

interface Props {
  pacienteId: string;
  citas?: Cita[];
}

const ESTADO_PRESUP_CFG: Record<string, { label: string; cls: string }> = {
  borrador:   { label: 'Borrador',   cls: 'bg-gray-100 text-gray-600' },
  enviado:    { label: 'Enviado',    cls: 'bg-blue-100 text-blue-700' },
  aceptado:   { label: 'Aceptado',  cls: 'bg-purple-100 text-purple-700' },
  en_curso:   { label: 'En curso',  cls: 'bg-amber-100 text-amber-700' },
  completado: { label: 'Completado',cls: 'bg-green-100 text-green-700' },
  cancelado:  { label: 'Cancelado', cls: 'bg-red-100 text-red-600' },
};

export function FinanzasTab({ pacienteId, citas = [] }: Props) {
  const [presupFormOpen, setPresupFormOpen] = useState(false);

  const { data: pagos = [], isLoading: loadingPagos } = useQuery<Pago[]>({
    queryKey: ['pagos', 'paciente', pacienteId],
    queryFn: () => api.get('/api/pagos', { params: { pacienteId } }).then(r => r.data),
  });

  const { data: servicios = [] } = useQuery<any[]>({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data).catch(() => []),
    staleTime: 60_000,
  });

  const { data: presupuestos = [] } = useQuery<any[]>({
    queryKey: ['presupuestos-paciente', pacienteId],
    queryFn: () => api.get(`/api/presupuestos/paciente/${pacienteId}`).then(r => r.data),
  });

  // Citas con servicio asignado y no canceladas
  const citasConServicio = citas.filter(
    c => c.servicioId && c.estado !== 'cancelada',
  );

  const totalPagado    = pagos.filter(p => p.estado === 'pagado').reduce((s, p) => s + Number(p.monto), 0);
  const totalPendiente = pagos.filter(p => p.estado === 'pendiente').reduce((s, p) => s + Number(p.monto), 0);

  if (loadingPagos) {
    return <p className="text-sm text-text-secondary text-center py-8">Cargando...</p>;
  }

  return (
    <div className="flex flex-col gap-6">

      {/* ── Resumen ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-green-100 bg-green-50/40 p-4 flex flex-col gap-1">
          <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Total pagado</p>
          <p className="text-2xl font-bold text-green-600">{fmtMonto(totalPagado)}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4 flex flex-col gap-1">
          <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Saldo pendiente</p>
          <p className={cn('text-2xl font-bold', totalPendiente > 0 ? 'text-amber-600' : 'text-text-secondary')}>
            {fmtMonto(totalPendiente)}
          </p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 flex flex-col gap-1">
          <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Servicios en citas</p>
          <p className="text-2xl font-bold text-text-primary">{citasConServicio.length}</p>
        </div>
      </div>

      {/* ── Servicios asignados en citas ── */}
      {citasConServicio.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
            <CalendarCheck size={13} className="text-primary-500" /> Servicios en citas agendadas
          </h4>
          <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
            {citasConServicio.map(cita => {
              const srv = servicios.find((s: any) => s.id === cita.servicioId);
              const precio = srv ? Number(srv.precio) : null;
              const fecha  = new Date(cita.fechaInicio);
              return (
                <div key={cita.id} className="flex items-center gap-4 px-4 py-3 bg-white">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
                    <CalendarCheck size={15} className="text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {cita.servicioNombre ?? 'Servicio'}
                      {precio !== null && (
                        <span className="ml-1.5 text-xs font-medium text-text-secondary">· {fmtMonto(precio)}</span>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-text-secondary">
                        {fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      {cita.medico && (
                        <>
                          <span className="text-xs text-text-secondary">·</span>
                          <p className="text-xs text-text-secondary/70 truncate">{cita.medico.nombre}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex-shrink-0">
                    <Clock size={11} />
                    Agendado
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Presupuestos ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide flex items-center gap-2">
            <ClipboardList size={13} className="text-primary-500" /> Presupuestos
          </h4>
          <button
            onClick={() => setPresupFormOpen(true)}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            <Plus size={12} /> Nuevo
          </button>
        </div>

        {presupuestos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center">
            <ClipboardList size={22} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin presupuestos</p>
            <button
              onClick={() => setPresupFormOpen(true)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-1"
            >
              Crear presupuesto
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
            {(presupuestos as any[]).map(p => {
              const cfg = ESTADO_PRESUP_CFG[p.estado] ?? ESTADO_PRESUP_CFG.borrador;
              const saldoPend = p.saldoPendiente ?? 0;
              return (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3 bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-text-primary truncate">{p.titulo}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${cfg.cls}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Total: {fmtMonto(p.total)}
                      {saldoPend > 0 && <> · <span className="text-amber-600">{fmtMonto(saldoPend)} pendiente</span></>}
                      {saldoPend === 0 && p.total > 0 && <> · <span className="text-green-600">Liquidado</span></>}
                    </p>
                  </div>
                  {saldoPend === 0 && p.total > 0
                    ? <CheckCircle size={15} className="text-green-500 flex-shrink-0" />
                    : <ChevronRight size={15} className="text-gray-300 flex-shrink-0" />
                  }
                </div>
              );
            })}
          </div>
        )}

        <PresupuestoFormDialog
          open={presupFormOpen}
          onOpenChange={setPresupFormOpen}
          pacienteId={pacienteId}
        />
      </div>

      {/* ── Pagos registrados ── */}
      <div>
        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
          <Receipt size={13} className="text-primary-500" /> Pagos registrados
        </h4>

        {pagos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center">
            <CreditCard size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin pagos registrados</p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
            {pagos.map(pago => {
              const estado = ESTADO_CONFIG[pago.estado] ?? ESTADO_CONFIG.pendiente;
              const EstadoIcon = estado.icon;
              const fecha = pago.pagadoEn ?? pago.createdAt;
              return (
                <div key={pago.id} className="flex items-center gap-4 px-4 py-3 bg-white hover:bg-gray-50/60 transition-colors">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
                    <CreditCard size={15} className="text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-text-primary">{fmtMonto(Number(pago.monto))}</p>
                      <span className="text-xs text-text-secondary">·</span>
                      <p className="text-xs text-text-secondary">{METODO_LABEL[pago.metodo] ?? pago.metodo}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-text-secondary">{formatDate(fecha)}</p>
                      {pago.consulta && (
                        <>
                          <span className="text-xs text-text-secondary">·</span>
                          <p className="text-xs text-text-secondary/70 italic">Consulta</p>
                        </>
                      )}
                      {pago.referencia && (
                        <>
                          <span className="text-xs text-text-secondary">·</span>
                          <p className="text-xs text-text-secondary/70 truncate max-w-[120px]">Ref: {pago.referencia}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={cn('flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0', estado.cls)}>
                    <EstadoIcon size={11} />
                    {estado.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Facturas fiscales (placeholder) ── */}
      <div>
        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
          <FileText size={13} className="text-primary-500" /> Facturas fiscales
        </h4>
        <div className="rounded-xl border border-dashed border-gray-200 px-5 py-6 flex items-center gap-4 text-text-secondary">
          <FileText size={28} className="text-gray-300 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-text-primary">Próximamente</p>
            <p className="text-xs mt-0.5">La emisión y consulta de facturas fiscales (CFDI) estará disponible en una próxima versión.</p>
          </div>
          <ChevronRight size={16} className="ml-auto flex-shrink-0 opacity-40" />
        </div>
      </div>

    </div>
  );
}
